import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, ShoppingCart, FileDown, CheckCircle2, Save, X, Lock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import ReportWorkflowTrail from '../../reports/ReportWorkflowTrail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CustomerListTab({ customers, atcCodes, onRefresh, fmt, isReadOnly = false }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredCustomers, setFilteredCustomers] = useState(customers);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showInactive, setShowInactive] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    
    const [form, setForm] = useState({
        customer_name: '',
        customer_code: '',
        registered_name: '',
        contact_person: '',
        terms: '',
        tin: '',
        address: '',
        zip_code: '',
        foreign_address: '',
        email1: '',
        email2: '',
        tel_no: '',
        mobile_no: '',
        fax_no: '',
        credit_limit: 0,
        is_vatable: false,
        has_wtax: false,
        atc_1: '',
        atc_2: '',
        atc_3: '',
        atc_4: '',
        active_subsidiaries: []
    });

    const getAtcSummary = (customer) => {
        const codes = [customer.atc_1, customer.atc_2, customer.atc_3, customer.atc_4].filter(Boolean);
        return codes.length ? codes.join(', ') : '-';
    };
    
    // Update filtered customers when customers or search changes
    useEffect(() => {
        let filtered = customers;
        
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(c => 
                c.customer_name?.toLowerCase().includes(query) ||
                c.customer_id?.toLowerCase().includes(query) ||
                c.customer_code?.toLowerCase().includes(query) ||
                c.tin?.toLowerCase().includes(query) ||
                c.contact_person?.toLowerCase().includes(query)
            );
        }
        
        if (!showInactive) {
            filtered = filtered.filter(c => c.is_active !== false);
        }
        
        setFilteredCustomers(filtered);
    }, [customers, searchQuery, showInactive]);
    
    // Reset form
    const resetForm = () => {
        setForm({
            customer_name: '',
            customer_code: '',
            registered_name: '',
            contact_person: '',
            terms: '',
            tin: '',
            address: '',
            zip_code: '',
            foreign_address: '',
            email1: '',
            email2: '',
            tel_no: '',
            mobile_no: '',
            fax_no: '',
            credit_limit: 0,
            is_vatable: false,
            has_wtax: false,
            atc_1: '',
            atc_2: '',
            atc_3: '',
            atc_4: '',
            active_subsidiaries: []
        });
        setSelectedCustomer(null);
        setIsEditing(false);
    };
    
    // Handle Add new customer
    const handleAdd = () => {
        if (isReadOnly) {
            toast.error('Customer Master is read-only in this review scope.');
            return;
        }
        resetForm();
        setIsEditing(true);
    };
    
    // Handle Edit customer
    const handleEdit = (customer) => {
        if (isReadOnly) {
            toast.error('Customer Master is read-only in this review scope.');
            return;
        }
        setSelectedCustomer(customer);
        setForm({
            customer_name: customer.customer_name || '',
            customer_code: customer.customer_code || '',
            registered_name: customer.registered_name || '',
            contact_person: customer.contact_person || '',
            terms: customer.terms || '',
            tin: customer.tin || '',
            address: customer.address || '',
            zip_code: customer.zip_code || '',
            foreign_address: customer.foreign_address || '',
            email1: customer.email1 || '',
            email2: customer.email2 || '',
            tel_no: customer.tel_no || '',
            mobile_no: customer.mobile_no || '',
            fax_no: customer.fax_no || '',
            credit_limit: customer.credit_limit || 0,
            is_vatable: customer.is_vatable || false,
            has_wtax: customer.has_wtax || false,
            atc_1: customer.atc_1 || '',
            atc_2: customer.atc_2 || '',
            atc_3: customer.atc_3 || '',
            atc_4: customer.atc_4 || '',
            active_subsidiaries: customer.active_subsidiaries || []
        });
        setIsEditing(true);
    };
    
    // Handle Cancel
    const handleCancel = () => {
        resetForm();
    };
    
    // Handle Save
    const handleSave = async () => {
        if (isReadOnly) {
            toast.error('Customer Master is read-only in this review scope.');
            return;
        }
        // Validation
        if (!form.customer_name.trim()) {
            toast.error('Customer name is required');
            return;
        }
        
        if ((form.is_vatable || form.has_wtax) && !form.tin.trim()) {
            toast.error('TIN is required when VATABLE or WTAX is enabled');
            return;
        }
        
        setSaving(true);
        try {
            const payload = {
                customer_name: form.customer_name.trim(),
                customer_code: form.customer_code.trim() || null,
                registered_name: form.registered_name.trim() || null,
                contact_person: form.contact_person.trim() || null,
                terms: form.terms || null,
                tin: form.tin.trim() || null,
                address: form.address.trim() || null,
                zip_code: form.zip_code.trim() || null,
                foreign_address: form.foreign_address.trim() || null,
                email1: form.email1.trim() || null,
                email2: form.email2.trim() || null,
                tel_no: form.tel_no.trim() || null,
                mobile_no: form.mobile_no.trim() || null,
                fax_no: form.fax_no.trim() || null,
                credit_limit: parseFloat(form.credit_limit) || 0,
                is_vatable: form.is_vatable,
                has_wtax: form.has_wtax,
                atc_1: form.atc_1 || null,
                atc_2: form.atc_2 || null,
                atc_3: form.atc_3 || null,
                atc_4: form.atc_4 || null,
                active_subsidiaries: form.active_subsidiaries
            };
            
            if (selectedCustomer) {
                await axios.put(`${API}/customers/${selectedCustomer.id}`, payload);
                setLastResult({
                    title: `Customer ${selectedCustomer.customer_id} updated`,
                    status: 'Active',
                    source: payload.customer_name,
                    reference: payload.tin || payload.terms || payload.customer_code || 'Customer profile',
                    location: 'Setup > Customer Master and AR customer dropdowns',
                    reviewState: 'Available for AR Invoice, AR Collections, and customer reports.',
                    glImpact: 'Master-data only. No GL, Trial Balance, AR balances, or reports change until used by a posted transaction.',
                    nextSafeAction: 'Use this customer on AR transactions only after confirming TIN, VAT/WTAX, terms, and contact details.'
                });
                toast.success(
                    `Customer ${selectedCustomer.customer_id} updated. Find it in Setup > Customer Master and AR customer dropdowns. No GL or Trial Balance impact until used in a posted transaction.`
                );
            } else {
                const response = await axios.post(`${API}/customers`, payload);
                const customerId = response.data?.customer_id || response.data?.id || 'new customer';
                setLastResult({
                    title: `Customer ${customerId} created`,
                    status: 'Active',
                    source: payload.customer_name,
                    reference: payload.tin || payload.terms || payload.customer_code || 'Customer profile',
                    location: 'Setup > Customer Master and AR customer dropdowns',
                    reviewState: 'Available for AR Invoice, AR Collections, and customer reports.',
                    glImpact: 'Master-data only. No GL, Trial Balance, AR balances, or reports change until used by a posted transaction.',
                    nextSafeAction: 'Use this customer on AR transactions only after confirming TIN, VAT/WTAX, terms, and contact details.'
                });
                toast.success(
                    `Customer ${customerId} created. Find it in Setup > Customer Master and AR customer dropdowns. No GL or Trial Balance impact until used in a posted transaction.`
                );
            }
            
            resetForm();
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save customer');
        }
        setSaving(false);
    };
    
    // Handle Delete
    const handleDelete = async (customer) => {
        if (isReadOnly) {
            toast.error('Customer Master is read-only in this review scope.');
            return;
        }
        if (!window.confirm(`Are you sure you want to deactivate customer "${customer.customer_name}"?`)) {
            return;
        }
        
        try {
            await axios.delete(`${API}/customers/${customer.id}`);
            setLastResult({
                title: `Customer ${customer.customer_id} deactivated`,
                status: 'Inactive',
                source: customer.customer_name,
                reference: customer.tin || customer.terms || customer.customer_code || 'Customer profile',
                location: 'Setup > Customer Master when Show Inactive is enabled',
                reviewState: 'Hidden from normal active customer selection; historical AR records remain linked.',
                glImpact: 'Master-data status only. Existing posted AR/GL/Trial Balance evidence is preserved.',
                nextSafeAction: 'Reactivate only if this customer should be available for new AR transactions again.'
            });
            toast.success(
                `Customer ${customer.customer_id} deactivated. It stays in Customer Master when Show Inactive is on; existing AR and GL history remains preserved.`
            );
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete customer');
        }
    };
    
    // Handle Activate
    const handleActivate = async (customer) => {
        if (isReadOnly) {
            toast.error('Customer Master is read-only in this review scope.');
            return;
        }
        try {
            await axios.put(`${API}/customers/${customer.id}/activate`);
            setLastResult({
                title: `Customer ${customer.customer_id} activated`,
                status: 'Active',
                source: customer.customer_name,
                reference: customer.tin || customer.terms || customer.customer_code || 'Customer profile',
                location: 'Setup > Customer Master and AR customer dropdowns',
                reviewState: 'Available again for AR Invoice, AR Collections, and customer reports.',
                glImpact: 'Master-data status only. No GL, Trial Balance, AR balances, or reports change until used by a posted transaction.',
                nextSafeAction: 'Confirm details before using this customer on new AR transactions.'
            });
            toast.success(
                `Customer ${customer.customer_id} activated. It is available again in Setup > Customer Master and AR customer dropdowns.`
            );
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to activate customer');
        }
    };
    
    // Handle Export to XLS
    const handleExport = async () => {
        try {
            const res = await axios.get(`${API}/customers/export/xls`);
            const data = res.data.data;
            
            if (data.length === 0) {
                toast.error('No data to export');
                return;
            }
            
            const headers = Object.keys(data[0]);
            const csvContent = [
                headers.join(','),
                ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
            ].join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = res.data.filename.replace('.xlsx', '.csv');
            link.click();
            
            toast.success(`Exported ${data.length} customers. Export only; no AR, GL, or Trial Balance records changed.`);
        } catch (error) {
            toast.error('Failed to export customers');
        }
    };
    
    return (
        <div className="space-y-4" data-testid="customer-list-tab">
            {isReadOnly && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="customer-master-readonly-scope">
                    Customer Master is read-only in this review scope. Use it to confirm AR source setup; changes should be made only from an editable workspace.
                </div>
            )}

            <ReportWorkflowTrail
                title="Customer Master trail"
                testId="customer-master-workflow-trail"
                scope={isReadOnly ? 'Read-only review' : 'Your setup workspace'}
                items={[
                    {
                        label: 'Source setup',
                        value: 'Customers define AR counterparties, billing terms, VAT/WTAX flags, and contact details.'
                    },
                    {
                        label: 'Where it goes',
                        value: 'Active customers appear in Setup > Customer Master and AR Invoice/Collection customer selectors.'
                    },
                    {
                        label: 'Review',
                        value: 'Check TIN, terms, VAT/WTAX, ATC, and contact details before using a customer in AR transactions.'
                    },
                    {
                        label: 'GL and TB',
                        value: 'Customer records alone do not affect GL or Trial Balance; only posted AR transactions do.'
                    },
                    {
                        label: 'Next safe action',
                        value: 'Use deactivate for obsolete customers. Existing posted AR evidence remains linked and preserved.'
                    }
                ]}
            />

            {lastResult && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950" data-testid="customer-master-result-trail">
                    <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                        <div>
                            <p className="font-semibold">{lastResult.title}</p>
                            <p className="mt-1 text-sky-800">Status: <span className="font-semibold">{lastResult.status}</span></p>
                        </div>
                        <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-sky-700">
                            Latest setup action
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <p><span className="font-semibold">Created/changed:</span> {lastResult.source}</p>
                        <p><span className="font-semibold">Reference:</span> {lastResult.reference}</p>
                        <p><span className="font-semibold">Find it now:</span> {lastResult.location}</p>
                        <p><span className="font-semibold">Review:</span> {lastResult.reviewState}</p>
                        <p><span className="font-semibold">Accounting impact:</span> {lastResult.glImpact}</p>
                        <p><span className="font-semibold">Next safe action:</span> {lastResult.nextSafeAction}</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-[#0F2A44] to-[#1E3A8A] px-6 py-4 text-white">
                    <h2 className="font-semibold font-['Manrope'] flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5" /> Customer Master List
                    </h2>
                    <p className="text-white/70 text-sm mt-1">Manage customers for AR, JV, and reports</p>
                </div>
                
                {/* Search & Actions */}
                <div className="p-4 border-b border-slate-200 flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Search by name, ID, code, TIN, contact..."
                                className="pl-10"
                                data-testid="customer-search"
                            />
                        </div>
                    </div>
                    
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={e => setShowInactive(e.target.checked)}
                            className="rounded border-slate-300"
                        />
                        Show Inactive
                    </label>
                    
                    <Button variant="outline" onClick={handleExport} data-testid="export-customers-btn">
                        <FileDown className="w-4 h-4 mr-2" /> Export XLS
                    </Button>
                    
                    {!isEditing && !isReadOnly && (
                        <Button onClick={handleAdd} className="bg-[#2F8FCB] hover:bg-[#2577a8]" data-testid="add-customer-btn">
                            <Plus className="w-4 h-4 mr-2" /> Add Customer
                        </Button>
                    )}
                </div>
                
                {/* Edit Form */}
                {isEditing && (
                    <div className="p-6 border-b border-slate-200 bg-green-50">
                        <h3 className="font-semibold text-slate-900 mb-4">
                            {selectedCustomer ? `Edit Customer: ${selectedCustomer.customer_id}` : 'New Customer'}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <Label>Customer ID</Label>
                                <Input
                                    value={selectedCustomer?.customer_id || '(Auto-generated)'}
                                    disabled
                                    className="mt-1 bg-slate-100"
                                />
                            </div>
                            <div>
                                <Label>Customer Code</Label>
                                <Input
                                    value={form.customer_code}
                                    onChange={e => setForm(prev => ({ ...prev, customer_code: e.target.value }))}
                                    placeholder="Optional code"
                                    className="mt-1"
                                    data-testid="customer-code"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Customer Name <span className="text-red-500">*</span></Label>
                                <Input
                                    value={form.customer_name}
                                    onChange={e => setForm(prev => ({ ...prev, customer_name: e.target.value }))}
                                    placeholder="Customer name"
                                    className="mt-1"
                                    data-testid="customer-name"
                                />
                            </div>
                            
                            <div className="md:col-span-2">
                                <Label>Registered Name</Label>
                                <Input
                                    value={form.registered_name}
                                    onChange={e => setForm(prev => ({ ...prev, registered_name: e.target.value }))}
                                    placeholder="Legal registered name"
                                    className="mt-1"
                                    data-testid="customer-registered-name"
                                />
                            </div>
                            <div>
                                <Label>Contact Person</Label>
                                <Input
                                    value={form.contact_person}
                                    onChange={e => setForm(prev => ({ ...prev, contact_person: e.target.value }))}
                                    placeholder="Contact name"
                                    className="mt-1"
                                    data-testid="customer-contact"
                                />
                            </div>
                            <div>
                                <Label>TIN {(form.is_vatable || form.has_wtax) && <span className="text-red-500">*</span>}</Label>
                                <Input
                                    value={form.tin}
                                    onChange={e => setForm(prev => ({ ...prev, tin: e.target.value }))}
                                    placeholder="000-000-000-000"
                                    className="mt-1"
                                    data-testid="customer-tin"
                                />
                            </div>
                            
                            <div className="md:col-span-2">
                                <Label>Address</Label>
                                <Input
                                    value={form.address}
                                    onChange={e => setForm(prev => ({ ...prev, address: e.target.value }))}
                                    placeholder="Full address"
                                    className="mt-1"
                                    data-testid="customer-address"
                                />
                            </div>
                            <div>
                                <Label>Zip Code</Label>
                                <Input
                                    value={form.zip_code}
                                    onChange={e => setForm(prev => ({ ...prev, zip_code: e.target.value }))}
                                    placeholder="Zip code"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Terms</Label>
                                <Select
                                    value={form.terms || 'none'}
                                    onValueChange={v => setForm(prev => ({ ...prev, terms: v === 'none' ? '' : v }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="customer-terms">
                                        <SelectValue placeholder="Select terms" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        <SelectItem value="COD">COD (Cash on Delivery)</SelectItem>
                                        <SelectItem value="Net 7">Net 7</SelectItem>
                                        <SelectItem value="Net 15">Net 15</SelectItem>
                                        <SelectItem value="Net 30">Net 30</SelectItem>
                                        <SelectItem value="Net 45">Net 45</SelectItem>
                                        <SelectItem value="Net 60">Net 60</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div>
                                <Label>Credit Limit</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.credit_limit}
                                    onChange={e => setForm(prev => ({ ...prev, credit_limit: e.target.value }))}
                                    placeholder="0.00"
                                    className="mt-1 font-mono text-right"
                                    data-testid="customer-credit-limit"
                                />
                            </div>
                            <div>
                                <Label>Email 1</Label>
                                <Input
                                    type="email"
                                    value={form.email1}
                                    onChange={e => setForm(prev => ({ ...prev, email1: e.target.value }))}
                                    placeholder="email@example.com"
                                    className="mt-1"
                                    data-testid="customer-email1"
                                />
                            </div>
                            <div>
                                <Label>Email 2</Label>
                                <Input
                                    type="email"
                                    value={form.email2}
                                    onChange={e => setForm(prev => ({ ...prev, email2: e.target.value }))}
                                    placeholder="secondary@example.com"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div>
                                <Label>Tel No.</Label>
                                <Input
                                    value={form.tel_no}
                                    onChange={e => setForm(prev => ({ ...prev, tel_no: e.target.value }))}
                                    placeholder="Telephone"
                                    className="mt-1"
                                    data-testid="customer-tel"
                                />
                            </div>
                            <div>
                                <Label>Mobile No.</Label>
                                <Input
                                    value={form.mobile_no}
                                    onChange={e => setForm(prev => ({ ...prev, mobile_no: e.target.value }))}
                                    placeholder="Mobile"
                                    className="mt-1"
                                    data-testid="customer-mobile"
                                />
                            </div>
                            <div>
                                <Label>Fax No.</Label>
                                <Input
                                    value={form.fax_no}
                                    onChange={e => setForm(prev => ({ ...prev, fax_no: e.target.value }))}
                                    placeholder="Fax"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div className="md:col-span-4 rounded-lg border border-slate-200 bg-white p-4">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-900">BIR / tax profile</h4>
                                        <p className="text-xs text-slate-500 mt-1">Used for AR VAT classification, withholding references, and future compliance exports.</p>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={form.is_vatable}
                                                onChange={e => setForm(prev => ({ ...prev, is_vatable: e.target.checked }))}
                                                className="rounded border-slate-300"
                                                data-testid="customer-vatable"
                                            />
                                            <span className="text-sm">VATABLE</span>
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={form.has_wtax}
                                                onChange={e => setForm(prev => ({ ...prev, has_wtax: e.target.checked }))}
                                                className="rounded border-slate-300"
                                                data-testid="customer-wtax"
                                            />
                                            <span className="text-sm">WTAX</span>
                                        </label>
                                    </div>
                                </div>
                                {(form.is_vatable || form.has_wtax) && (
                                    <p className="mt-3 text-xs font-medium text-amber-700">
                                        TIN is required when VATABLE or WTAX is enabled.
                                    </p>
                                )}
                            </div>
                            
                            {/* ATC Dropdowns */}
                            <div>
                                <Label>ATC 1</Label>
                                <Select
                                    value={form.atc_1 || 'none'}
                                    onValueChange={v => setForm(prev => ({ ...prev, atc_1: v === 'none' ? '' : v }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="customer-atc1">
                                        <SelectValue placeholder="Select ATC" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {atcCodes.map(atc => (
                                            <SelectItem key={atc.code} value={atc.code}>
                                                {atc.code} - {atc.desc} ({atc.rate}%)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>ATC 2</Label>
                                <Select
                                    value={form.atc_2 || 'none'}
                                    onValueChange={v => setForm(prev => ({ ...prev, atc_2: v === 'none' ? '' : v }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="customer-atc2">
                                        <SelectValue placeholder="Select ATC" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {atcCodes.map(atc => (
                                            <SelectItem key={atc.code} value={atc.code}>
                                                {atc.code} - {atc.desc} ({atc.rate}%)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>ATC 3</Label>
                                <Select
                                    value={form.atc_3 || 'none'}
                                    onValueChange={v => setForm(prev => ({ ...prev, atc_3: v === 'none' ? '' : v }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="customer-atc3">
                                        <SelectValue placeholder="Select ATC" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {atcCodes.map(atc => (
                                            <SelectItem key={atc.code} value={atc.code}>
                                                {atc.code} - {atc.desc} ({atc.rate}%)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>ATC 4</Label>
                                <Select
                                    value={form.atc_4 || 'none'}
                                    onValueChange={v => setForm(prev => ({ ...prev, atc_4: v === 'none' ? '' : v }))}
                                >
                                    <SelectTrigger className="mt-1" data-testid="customer-atc4">
                                        <SelectValue placeholder="Select ATC" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {atcCodes.map(atc => (
                                            <SelectItem key={atc.code} value={atc.code}>
                                                {atc.code} - {atc.desc} ({atc.rate}%)
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 mt-6">
                            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-customer-btn">
                                <Save className="w-4 h-4 mr-2" /> {selectedCustomer ? 'Update' : 'Save'}
                            </Button>
                            <Button variant="outline" onClick={handleCancel} disabled={saving} data-testid="cancel-customer-btn">
                                <X className="w-4 h-4 mr-2" /> Cancel
                            </Button>
                        </div>
                    </div>
                )}
                
                {/* Customer Grid */}
                {filteredCustomers.length === 0 ? (
                    <div className="p-12 text-center">
                        <ShoppingCart className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500">No customers found</p>
                        <p className="text-sm text-slate-400 mt-1">
                            {isReadOnly ? 'No customers are visible in this review scope' : 'Click "Add Customer" to create your first customer'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 text-left">Customer ID</th>
                                    <th className="px-4 py-3 text-left">Name</th>
                                    <th className="px-4 py-3 text-left">Contact</th>
                                    <th className="px-4 py-3 text-left">TIN</th>
                                    <th className="px-4 py-3 text-left">Terms</th>
                                    <th className="px-4 py-3 text-right">Credit Limit</th>
                                    <th className="px-4 py-3 text-center">VAT</th>
                                    <th className="px-4 py-3 text-center">WTAX</th>
                                    <th className="px-4 py-3 text-left">ATC</th>
                                    <th className="px-4 py-3 text-center">Status</th>
                                    <th className="px-4 py-3 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredCustomers.map((customer) => (
                                    <tr key={customer.id} className={`hover:bg-slate-50 ${!customer.is_active ? 'opacity-50' : ''}`} data-testid={`customer-row-${customer.id}`}>
                                        <td className="px-4 py-3 font-mono text-sm">{customer.customer_id}</td>
                                        <td className="px-4 py-3 font-medium">{customer.customer_name}</td>
                                        <td className="px-4 py-3 text-sm text-slate-600">{customer.contact_person || '-'}</td>
                                        <td className="px-4 py-3 font-mono text-sm">{customer.tin || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{customer.terms || '-'}</td>
                                        <td className="px-4 py-3 text-right font-mono text-sm">{fmt(customer.credit_limit || 0)}</td>
                                        <td className="px-4 py-3 text-center">
                                            {customer.is_vatable && <span className="text-emerald-600">✓</span>}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {customer.has_wtax && <span className="text-emerald-600">✓</span>}
                                        </td>
                                        <td className="px-4 py-3 text-xs font-mono text-slate-600">{getAtcSummary(customer)}</td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                customer.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                                            }`}>
                                                {customer.is_active !== false ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                {isReadOnly ? (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button className="p-1.5 text-slate-300 cursor-not-allowed rounded-lg" disabled data-testid={`readonly-customer-${customer.id}`}>
                                                                <Lock className="w-4 h-4" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Read-only review</TooltipContent>
                                                    </Tooltip>
                                                ) : (
                                                    <>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button
                                                                    onClick={() => handleEdit(customer)}
                                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                                                                    data-testid={`edit-customer-${customer.id}`}
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Edit</TooltipContent>
                                                        </Tooltip>

                                                        {customer.is_active !== false ? (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        onClick={() => handleDelete(customer)}
                                                                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                                                        data-testid={`delete-customer-${customer.id}`}
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Deactivate</TooltipContent>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        onClick={() => handleActivate(customer)}
                                                                        className="p-1.5 text-emerald-500 hover:bg-emerald-50 rounded-lg"
                                                                    >
                                                                        <CheckCircle2 className="w-4 h-4" />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Activate</TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                
                {/* Footer Stats */}
                <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 text-sm text-slate-600">
                    Showing {filteredCustomers.length} of {customers.length} customers
                </div>
            </div>
        </div>
    );
}


export default CustomerListTab;
